import Button from "@components/ui/Button";
import Spinner from "@components/ui/Spinner";
import ErrorBanner from "@components/shared/ErrorBanner";
import Table, {
  TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
} from "@components/ui/Table";
import { useReactivationCampaigns } from "@hooks/useReactivationCampaigns";
import { CAMPAIGN_STATUS, COUNTER_ORDER, modeLabel } from "@components/scheduling/campaignLabels";
import CampaignDetailDrawer from "@components/scheduling/CampaignDetailDrawer";

/** Progress as a sentence: only the counters that are not zero. */
function Progress({ counters }) {
  const parts = COUNTER_ORDER
    .filter(([key]) => (counters?.[key] || 0) > 0)
    .map(([key, label]) => `${counters[key]} ${label.toLowerCase()}`);
  if (parts.length === 0) return <span className="text-gray-400">Not started</span>;
  return <span>{parts.join(", ")}</span>;
}

export default function ReactivationCampaignsTab({ clinicId }) {
  const hook = useReactivationCampaigns(clinicId);

  return (
    <div className="space-y-4">
      {hook.error && <ErrorBanner message={hook.error} onDismiss={hook.clearError} />}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Campaigns call the patients you selected one at a time, and try again on the
          clinic's protocol when nobody answers.
        </p>
        <Button color="light" size="sm" onClick={() => hook.refresh()} disabled={hook.loading}>
          {hook.loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {hook.loading && hook.campaigns.length === 0 ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : hook.campaigns.length === 0 ? (
        // An empty list after a failed request says nothing about the clinic:
        // the banner above already explains it, and telling a clinic that has
        // campaigns it has none would be worse than saying nothing.
        hook.error ? null : (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
            <p className="text-sm text-gray-600">
              No campaigns yet. Pick patients on the Call list tab to start one.
            </p>
          </div>
        )
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Campaign</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Patients</TableHeaderCell>
              <TableHeaderCell>Progress</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hook.campaigns.map((campaign) => {
              const meta = CAMPAIGN_STATUS[campaign.status] || {};
              return (
                <TableRow key={campaign.id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => hook.openCampaign(campaign)}>
                  <TableCell className="text-sm font-medium text-gray-900">
                    {campaign.name}
                    <span className="block text-xs text-gray-500">
                      {campaign.treatment_name}
                    </span>
                    <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                      {modeLabel(campaign)}
                    </span>
                    {campaign.provider_name && (
                      <span className="block text-xs text-gray-500">
                        With {campaign.provider_name}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      meta.className || "bg-gray-100 text-gray-700"}`}>
                      {meta.label || campaign.status}
                    </span>
                    {campaign.pause_reason && (
                      <span className="block text-xs text-amber-700">
                        {campaign.pause_reason}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {campaign.counters?.total ?? 0}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    <Progress counters={campaign.counters} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <CampaignDetailDrawer detail={hook.detail} loading={hook.detailLoading}
                            onClose={hook.closeCampaign} onControl={hook.control}
                            controlError={hook.controlError}
                            onDismissControlError={hook.clearControlError} />
    </div>
  );
}
